import { useEffect, useRef, MutableRefObject, memo } from "react";
import { cn } from "@/lib/utils";

interface WaveformVisualizerProps {
  isPlaying: boolean;
  /** Kept for potential fallback; visualizer currently reads from analyserNode directly. */
  audioLevelRef: MutableRefObject<number>;
  analyserNode: AnalyserNode | null;
  className?: string;
}

const NUM_BARS = 64;
const ATTACK_FACTOR = 0.4;
const DECAY_FACTOR = 0.06;
const LOG_EXPONENT = 1.3;

export const WaveformVisualizer = memo(function WaveformVisualizer({
  isPlaying,
  audioLevelRef: _audioLevelRef,
  analyserNode,
  className,
}: WaveformVisualizerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const sizeRef = useRef({ width: 0, height: 0, dpr: 1 });
  const smoothedRef = useRef<Float32Array>(new Float32Array(NUM_BARS).fill(0));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) return;

    // Reset smoothed bars when analyserNode changes (reconnect)
    smoothedRef.current.fill(0);

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      sizeRef.current = { width: rect.width, height: rect.height, dpr };
    };

    const bgColor = getComputedStyle(canvas).getPropertyValue("--background").trim();
    const clearColor = bgColor ? `hsl(${bgColor})` : "#0a0e14";

    const fftBuffer = analyserNode ? new Uint8Array(analyserNode.frequencyBinCount) : null;

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const draw = () => {
      const { width, height } = sizeRef.current;
      if (width === 0) {
        animationRef.current = requestAnimationFrame(draw);
        return;
      }

      ctx.fillStyle = clearColor;
      ctx.fillRect(0, 0, width, height);

      const centerY = height / 2;

      if (fftBuffer && analyserNode) {
        analyserNode.getByteFrequencyData(fftBuffer);
      }

      // Aggregate FFT bins into NUM_BARS bands, smoothed across frames
      const smoothed = smoothedRef.current;
      const binCount = fftBuffer ? fftBuffer.length : 0;

      for (let i = 0; i < NUM_BARS; i++) {
        let target = 0;
        if (fftBuffer && isPlaying && binCount > 0) {
          // Map bar index to FFT bins with slight log weighting
          // so bass gets a few more bars than treble
          const t0 = i / NUM_BARS;
          const t1 = (i + 1) / NUM_BARS;
          const bin0 = Math.floor(Math.pow(t0, LOG_EXPONENT) * binCount);
          const bin1 = Math.max(bin0 + 1, Math.floor(Math.pow(t1, LOG_EXPONENT) * binCount));

          let sum = 0;
          for (let b = bin0; b < bin1 && b < binCount; b++) {
            sum += fftBuffer[b];
          }
          target = sum / ((bin1 - bin0) * 255);
        }

        // Fast attack, slow decay
        const factor = target > smoothed[i] ? ATTACK_FACTOR : DECAY_FACTOR;
        smoothed[i] += (target - smoothed[i]) * factor;
      }

      // Draw mirrored bars from center
      const gap = 2;
      const barWidth = (width - gap * (NUM_BARS - 1)) / NUM_BARS;
      const maxBarHeight = (height / 2) * 0.85;

      for (let i = 0; i < NUM_BARS; i++) {
        const x = i * (barWidth + gap);
        const mag = smoothed[i];
        const barH = mag * maxBarHeight;

        if (barH < 0.5) continue;

        // Warm highs upward
        ctx.globalAlpha = 0.7 + mag * 0.3;
        ctx.fillStyle = "#f59e0b";
        ctx.fillRect(x, centerY - barH, barWidth, barH);

        // Cool lows downward (slightly shorter for depth)
        ctx.globalAlpha = 0.4 + mag * 0.2;
        ctx.fillStyle = "#22d3ee";
        ctx.fillRect(x, centerY, barWidth, barH * 0.8);
      }

      // Subtle center line
      ctx.globalAlpha = 0.1;
      ctx.fillStyle = "#f59e0b";
      ctx.fillRect(0, centerY - 0.5, width, 1);

      ctx.globalAlpha = 1;
      animationRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      ro.disconnect();
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [analyserNode, isPlaying]);

  return (
    <canvas
      ref={canvasRef}
      className={cn("w-full h-full", className)}
    />
  );
});
